import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ProjectKeyValEntry, QUIZ_HISTORY_TYPE, QuizAnswerLogEntry } from '@shared/ProjectKeyValStore';
import { TableModule } from 'primeng/table';
import { Subscription } from 'rxjs';
import { ProjectKeyvalAdminService } from 'src/app/shared/services/project-keyval-admin.service';
import { ProjectService } from 'src/app/shared/services/project.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { ProjectSubmenuComponent } from 'src/app/project-submenu/project-submenu.component';

export interface QuizUserSummary {
    userId: string;
    userName: string;
    askedCount: number;
    rightCount: number;
    wrongCount: number;
    lastActivity: string;
}

@Component({
    imports: [DatePipe, TableModule, ProjectSubmenuComponent],
    templateUrl: './project-quiz-tab.component.html'
})
export class ProjectQuizTabComponent implements OnInit, OnDestroy {
    private readonly _userSummaries = signal<QuizUserSummary[]>([]);
    public get userSummaries(): QuizUserSummary[] { return this._userSummaries(); }
    public set userSummaries(value: QuizUserSummary[]) { this._userSummaries.set(value); }
    private keyvals: ProjectKeyValEntry<string>[] = [];
    private userMap: Record<string, { name: string; guestId?: string }> = {};
    private subs: Subscription[] = [];

    constructor(
        private adminService: ProjectKeyvalAdminService,
        private projectService: ProjectService,
        private socketService: SocketAdminService,
        private router: Router
    ) { }

    private get projectId(): string {
        return this.projectService.selectedProject?._id ?? '-';
    }

    public async ngOnInit() {
        await this.loadData();

        this.subs.push(
            this.socketService.onProjectKeyValChange.subscribe((entry) => {
                if (entry.projectId != this.projectService.selectedProject?._id) return;
                if (entry.type !== QUIZ_HISTORY_TYPE) return;
                const strEntry = { ...entry, value: String(entry.value) } as ProjectKeyValEntry<string>;
                const index = this.keyvals.findIndex(kv => kv._id === entry._id);
                if (index > -1) {
                    this.keyvals[index] = strEntry;
                } else {
                    this.keyvals.push(strEntry);
                }
                this.buildSummaries();
            }),
            this.socketService.onProjectKeyValClean.subscribe(() => {
                void this.loadData();
            }),
            this.socketService.onProjectKeyValDelete.subscribe(() => {
                void this.loadData();
            })
        );
    }

    public ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    private async loadData() {
        const [keyvals, users] = await Promise.all([
            this.adminService.getProjectKeyVals(this.projectId),
            this.adminService.getProjectKeyValUsers(this.projectId)
        ]);
        this.keyvals = keyvals
            .filter(kv => kv.type === QUIZ_HISTORY_TYPE)
            .map(kv => ({ ...kv, value: String(kv.value) }));
        this.userMap = users;
        this.buildSummaries();
    }

    private parseEntry(kv: ProjectKeyValEntry<string>): QuizAnswerLogEntry | null {
        try {
            return JSON.parse(kv.value) as QuizAnswerLogEntry;
        } catch {
            return null;
        }
    }

    private getUserName(userId: string): string {
        if (!userId) return '';
        const user = this.userMap[userId];
        if (user?.name) return user.name;
        if (user?.guestId) return user.guestId;
        return userId;
    }

    private buildSummaries() {
        const byUser = new Map<string, { asked: number; right: number; wrong: number; last: string }>();
        for (const kv of this.keyvals) {
            const entry = this.parseEntry(kv);
            if (!entry) continue;
            if (!byUser.has(kv.userId)) {
                byUser.set(kv.userId, { asked: 0, right: 0, wrong: 0, last: '' });
            }
            const agg = byUser.get(kv.userId);
            if (!agg) continue;
            agg.asked++;
            if (entry.correct) {
                agg.right++;
            } else {
                agg.wrong++;
            }
            if (entry.answeredAt > agg.last) {
                agg.last = entry.answeredAt;
            }
        }
        this.userSummaries = Array.from(byUser.entries())
            .map(([userId, agg]) => ({
                userId,
                userName: this.getUserName(userId),
                askedCount: agg.asked,
                rightCount: agg.right,
                wrongCount: agg.wrong,
                lastActivity: agg.last
            }))
            .sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
    }

    public openUser(userId: string) {
        void this.router.navigate(['/bot-admin/project', this.projectId, 'quiz', userId]);
    }
}
